import { useEffect } from "react";
import { useLocation } from "react-router-dom-v5-compat";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getTitleKeyForPath } from "./titleMappings";

/**
 * Hook to automatically update the document title based on the current URL path.
 * Titles are translated using the i18n system from rbc.translation.json files.
 *
 * Also handles checkout state query parameters (e.g., ?checkoutState=bookingConfirmation)
 * to show appropriate titles during checkout flows.
 *
 * Usage:
 * ```tsx
 * // In your App component (must be within I18nProvider)
 * useDocumentTitle();
 * ```
 */
export function useDocumentTitle() {
  const location = useLocation();
  const { t } = useI18nContext();

  useEffect(() => {
    // Check for checkout state query parameter
    const searchParams = new URLSearchParams(location.search);
    const checkoutState = searchParams.get("checkoutState");

    // Handle booking confirmation state in checkout flows
    // All checkout flows use ?checkoutState=bookingConfirmation when showing the confirmation
    if (
      checkoutState === "bookingConfirmation" &&
      (location.pathname.includes("/book/") ||
        location.pathname.includes("/checkout/") ||
        location.pathname.endsWith("/book") ||
        location.pathname.endsWith("/checkout"))
    ) {
      // Get the base title key for the current path (e.g., "pageTitles.flights.book")
      // and replace the last segment with "confirmation"
      const baseTitleKey = getTitleKeyForPath(location.pathname);
      if (baseTitleKey) {
        // Replace .book or .checkout with .confirmation
        const confirmationKey = baseTitleKey.replace(
          /\.(book|checkout)$/,
          ".confirmation"
        );
        document.title = t(confirmationKey);
        return;
      }
    }

    // Otherwise use path-based title mapping
    const titleKey = getTitleKeyForPath(location.pathname);

    if (titleKey) {
      const translatedTitle = t(titleKey);
      document.title = translatedTitle;
    } else {
      // Fallback to home title for unmapped routes
      document.title = t("pageTitles.home");
    }
  }, [location.pathname, location.search, t]);
}
